import { useMutation } from '@tanstack/react-query';
import queryClient from '@app/queryClient';
import { toast } from '@huspy/forge/shared';
import { t } from 'i18next';
import { addToAmplitudeEventContext, trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { BankApplication } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import { SubmitOpportunityRequestDTO } from '../api/dto/submitOpportunity.dto';
import { opportunitiesApi } from '../api';

const trackEvent = (
  event: string,
  body: SubmitOpportunityRequestDTO,
  opportunityId: string,
  clientId: string,
  bankApplications: BankApplication[],
  errorMessage?: string
) => {
  const individualSubmission = body.bank_applications && body.bank_applications.length > 0;
  const startRank = bankApplications
    .filter((bankApplication) => bankApplication.status !== BANK_APPLICATION_STATUS.draft)
    .length;
  const applicationRank = individualSubmission ? (startRank + 1) : 0;

  trackAmplitudeEvent(event, {
    useContext: true,
    addToContext: event === USER_EVENTS.CASE.CASE_SUBMISSION_ATTEMPTED,
    case_id: opportunityId,
    client_id: clientId,
    banks: undefined,
    destination_type: 'Huspy',
    destinations: ['Huspy'],
    application_rank: applicationRank,
    error_message: errorMessage ?? undefined,
  });
};

const useSubmitOpportunity = (opportunityId: string, clientId: string, bankApplications: BankApplication[] = []) => {
  const { isPending, mutateAsync, isSuccess } = useMutation({
    mutationKey: ['submitOpportunity', opportunityId],
    mutationFn: (body: SubmitOpportunityRequestDTO) => {
      // Track attempt
      addToAmplitudeEventContext({ application_rank: undefined });
      trackEvent(
        USER_EVENTS.CASE.CASE_SUBMISSION_ATTEMPTED,
        body,
        opportunityId,
        clientId,
        bankApplications
      );

      return opportunitiesApi.submitOpportunity(opportunityId, body);
    },
    onSuccess: (_, body) => {
      // Track success
      trackEvent(
        USER_EVENTS.CASE.CASE_SUBMISSION_COMPLETED,
        body,
        opportunityId,
        clientId,
        bankApplications
      );

      toast('info', { message: t('opportunity.mutations.opportunitySubmittedSuccess') });
      queryClient.refetchQueries({ queryKey: ['clientOpportunity', opportunityId] });
      queryClient.refetchQueries({ queryKey: ['clientDetails', clientId] });
      queryClient.refetchQueries({ queryKey: ['listOpportunities', clientId] });
      queryClient.invalidateQueries({ queryKey: ['listAllOpportunities'] });
    },
    onError: (err, body) => {
      // Track error
      trackEvent(
        USER_EVENTS.CASE.CASE_SUBMISSION_FAILED,
        body,
        opportunityId,
        clientId,
        bankApplications,
        err.message ?? ''
      );

      if (err.data.message) {
        toast('error', { message: err.data.message });
        queryClient.refetchQueries({ queryKey: ['clientOpportunity', opportunityId] });
      } else {
        toast('error', { message: t('opportunity.mutations.opportunitySubmittedFail') });
      }
    },
  });

  return { mutate: mutateAsync, isPending, isSuccess };
};

export default useSubmitOpportunity;
